import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import notificationService from '../services/notificationService';
import { useAuth } from './AuthContext';

export interface AccountsNotification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  category?: 'system' | 'user' | 'security' | 'update' | 'approval' | 'review';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  entityType?: string;
  entityId?: string;
  actionData?: Record<string, any>;
  sender?: {
    id: string;
    name: string;
    email: string;
  };
}

interface AccountsNotificationContextType {
  notifications: AccountsNotification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAsUnread: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refetchNotifications: () => Promise<void>;
}

const AccountsNotificationContext = createContext<AccountsNotificationContextType | undefined>(undefined);

const ACCOUNTS_APP_NAME = 'accounts';

export const AccountsNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<AccountsNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  }, []);

  const markAsUnread = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsUnread(notificationId);
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: false }
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      setError('Failed to mark notification as unread');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const result = await notificationService.markAllAsRead({ appName: ACCOUNTS_APP_NAME });
      if (result.success) {
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, read: true }))
        );
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((notification) => notification.id !== notificationId)
      );
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    try {
      const result = await notificationService.clearAllNotifications({ appName: ACCOUNTS_APP_NAME });
      if (result.success) {
        setNotifications([]);
      }
    } catch (err) {
      console.error('Error clearing notifications:', err);
      setError('Failed to clear notifications');
    }
  }, []);

  const refetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await notificationService.getNotifications({ appName: ACCOUNTS_APP_NAME });
      if (response.success) {
        const transformedNotifications: AccountsNotification[] = response.data.map((item: any) => ({
          id: item.id,
          title: item.title,
          message: item.message,
          type: item.type,
          timestamp: new Date(item.timestamp),
          read: item.read,
          actionUrl: item.actionUrl,
          actionText: item.actionText,
          category: item.category,
          priority: item.priority,
          entityType: item.entityType,
          entityId: item.entityId,
          actionData: item.actionData,
          sender: item.sender,
        }));
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Error fetching accounts notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [isAuthenticated, refetchNotifications]);

  const contextValue = useMemo<AccountsNotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    ]
  );

  return (
    <AccountsNotificationContext.Provider value={contextValue}>
      {children}
    </AccountsNotificationContext.Provider>
  );
};

export const useAccountsNotifications = (): AccountsNotificationContextType => {
  const context = useContext(AccountsNotificationContext);

  if (context === undefined) {
    throw new Error('useAccountsNotifications must be used within a AccountsNotificationProvider');
  }

  return context;
};
